"use client";

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Cropper, { type Area } from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { Loader2 } from "lucide-react";
import { getCroppedBlob, type CropOutputOptions } from "@/lib/cropImage";

export interface ImageCropModalProps {
  open: boolean;
  imageSrc: string | null;
  aspect?: number;
  cropShape?: "rect" | "round";
  title?: string;
  confirmLabel?: string;
  /** External busy flag (e.g. upload in flight) — disables the controls. */
  isProcessing?: boolean;
  output?: CropOutputOptions;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void | Promise<void>;
}

export default function ImageCropModal({
  open,
  imageSrc,
  aspect = 1,
  cropShape = "round",
  title = "Crop image",
  confirmLabel = "Save",
  isProcessing = false,
  output,
  onCancel,
  onConfirm,
}: ImageCropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [generating, setGenerating] = useState(false);

  // Reset the transform every time a new image is opened.
  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setAreaPixels(null);
    }
  }, [open, imageSrc]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Capture phase + stopImmediatePropagation so Escape closes only the
        // cropper, not a background modal that also listens on document.
        e.stopImmediatePropagation();
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onCancel]);

  const onCropComplete = useCallback((_area: Area, areaPx: Area) => {
    setAreaPixels(areaPx);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !areaPixels) return;
    try {
      setGenerating(true);
      const blob = await getCroppedBlob(imageSrc, areaPixels, output);
      await onConfirm(blob);
    } catch (e) {
      console.error("Failed to crop image", e);
    } finally {
      setGenerating(false);
    }
  };

  if (!open || !imageSrc) return null;

  const busy = generating || isProcessing;

  return createPortal(
    <div
      // Marks this portaled overlay so background modals that close on
      // outside-click (e.g. SettingsModal) ignore interactions with the cropper.
      data-image-crop-modal="true"
      // Bottom sheet on mobile (docks to the bottom edge), centered dialog on
      // md+ — matches the app-wide modal convention (items-end md:items-center).
      className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/70 backdrop-blur-sm md:items-center md:p-4"
    >
      <div
        className="relative w-full overflow-hidden rounded-t-2xl bg-white shadow-2xl ring-1 ring-black/10 md:max-w-[440px] md:rounded-2xl dark:bg-zinc-900 dark:ring-white/10"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        title={title}
      >
        {/* Immersive crop stage — no chrome, image fills edge to edge. Zoom is
            handled by scroll/pinch (zoomWithScroll) instead of a slider. The
            dvh cap keeps the stage from overflowing short/landscape phones. */}
        <div className="relative h-[400px] max-h-[60dvh] w-full bg-zinc-950 md:max-h-none">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={1}
            maxZoom={3}
            aspect={aspect}
            cropShape={cropShape}
            showGrid={false}
            restrictPosition
            zoomWithScroll
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3.5">
          {/* <span className="hidden text-xs text-zinc-400 dark:text-zinc-500 sm:block">
            Drag to reposition · scroll to zoom
          </span> */}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="h-9 rounded-lg px-4 text-sm font-medium text-zinc-600 hover-component disabled:opacity-50 dark:text-zinc-300 "
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy || !areaPixels}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-500 px-5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? "Saving..." : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
